import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Placeholder from 'flarum/common/components/Placeholder';
import Link from 'flarum/common/components/Link';
import Button from 'flarum/common/components/Button';
import extractText from 'flarum/common/utils/extractText';
import speciesName from '../utils/speciesName';
import authorDisplayName from '../utils/authorDisplayName';
import SpeciesSearchWidget from './SpeciesSearchWidget';
const PAGE_LIMIT = 50;
const VISIBLE_STEP = 30;

// champis-forum's startDiscussion(): the composer with the Descriptions
// category preselected (or the log-in modal). Looked up at click time
// through flarum.reg: extensions can't import each other's source.
function startDiscussion(tagSlug) {
  window.flarum.reg.get('champis-forum', 'forum/utils/startDiscussion')?.(tagSlug);
}

// Strips accents so "amanit" also matches "Amanite" - a plain substring
// search wouldn't, and users searching a French common-ish spelling of a
// Latin name shouldn't have to type accents right to find anything.
function normalize(text) {
  return text.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();
}

// Species::insertRankMarker() (PHP) splices "var."/"f."/"subsp." into
// canonicalName between the two epithets it belongs between, so e.g. a
// form and a variety of the same infraspecific epithet read as different
// names (see speciesName.js). Sorting on that as-is would group entries
// by marker letter first ("f." before "subsp." before "var."), splitting
// up what should otherwise interleave as one alphabetical run by epithet
// - stripped back out here for the sort key only, the *displayed* name
// still goes through speciesName() and keeps its marker.
function sortKey(canonicalName) {
  return normalize(canonicalName.replace(/\s(subsp\.|var\.|f\.)\s/, ' '));
}

/**
 * /descriptions - lists every discussion tagged "descriptions" that has a
 * species linked to it (see champis-species' own `species` field on
 * DiscussionResource), matching champis.net/descriptions: species name
 * (+ French vernacular name when GBIF/TAXREF has one, same as the homepage's
 * RandomDescriptions widget - see the `vernacularName: 'true'` request
 * param below, which opts into that extra lookup) + author as the row,
 * linking to /description/:id (DescriptionPage.js) rather than the regular
 * discussion page.
 *
 * Loads the whole (small, ~230-discussion) set up front rather than using
 * the usual DiscussionListState + `filter[q]` search - Flarum's built-in
 * `q` filter only matches whole words (confirmed: "meotto" returns nothing
 * for "Meottomyces"), which makes it useless for an as-you-type species
 * search. With everything already in hand, filtering and alphabetising by
 * species name is just a plain client-side array operation instead.
 * VISIBLE_STEP only controls how many of the (possibly search-filtered)
 * results are actually *rendered* at once - "Charger davantage" reveals
 * more of what's already in memory, no extra request (same as /keys' own
 * DeterminationKeysPage.js).
 */
export default class DescriptionsPage extends Page {
  query = '';
  discussions = null;
  error = false;
  visibleCount = (() => VISIBLE_STEP)();
  oninit(vnode) {
    super.oninit(vnode);
    app.setTitle(app.translator.trans('champis-species.forum.descriptions_page.title'));
    this.query = '';
    this.discussions = null;
    this.error = false;
    this.visibleCount = VISIBLE_STEP;
    this.loadAll();
  }
  loadAll() {
    this.discussions = null;
    this.error = false;
    const url = app.forum.attribute('apiUrl') + '/discussions';
    const pageParams = offset => ({
      filter: {
        tag: 'descriptions'
      },
      page: {
        limit: PAGE_LIMIT,
        offset
      },
      include: 'user',
      vernacularName: 'true',
      withImages: 'true'
    });

    // Each discussion's `species` field can mean a live GBIF lookup
    // server-side (cache-cold worst case ~200-500ms *per discussion*, see
    // champis-species' DiscussionResource field) - across ~230 discussions
    // that adds up fast if pages are fetched one after another, so once the
    // first page reports the real total (`meta.page.total` - a plain field,
    // unlike `links.next`, see below), every remaining page is requested
    // *concurrently* instead: wall-clock cost becomes the slowest single
    // page rather than the sum of all of them. Sorting only once everything
    // is back also avoids re-sorting (and visibly reshuffling on screen)
    // after every page the way fetching them one at a time did before.
    //
    // Paginate by offset ourselves rather than following the response's own
    // `links.next` - in this environment that comes back missing the `/api`
    // prefix (an unrelated server/proxy quirk), so it 404s.
    app.request({
      method: 'GET',
      url,
      params: pageParams(0)
    }).then(firstPayload => {
      const total = firstPayload.meta?.page?.total ?? 0;
      const firstPage = app.store.pushPayload(firstPayload);
      const remainingOffsets = [];
      for (let offset = PAGE_LIMIT; offset < total; offset += PAGE_LIMIT) {
        remainingOffsets.push(offset);
      }
      Promise.all(remainingOffsets.map(offset => app.request({
        method: 'GET',
        url,
        params: pageParams(offset)
      }))).then(remainingPayloads => {
        const all = remainingPayloads.reduce((acc, payload) => acc.concat(app.store.pushPayload(payload)), firstPage);
        this.discussions = all.filter(d => d.species() && d.species().length).sort((a, b) => sortKey(a.species()[0].canonicalName).localeCompare(sortKey(b.species()[0].canonicalName)));
        m.redraw();
      }).catch(() => {
        this.error = true;
        m.redraw();
      });
    }).catch(() => {
      this.error = true;
      m.redraw();
    });
  }
  view() {
    const rows = this.discussions ? this.query.trim() ? this.discussions.filter(d => {
      const s = d.species()[0];
      return normalize(s.canonicalName + ' ' + s.scientificName).includes(normalize(this.query.trim()));
    }) : this.discussions : [];
    const visible = rows.slice(0, this.visibleCount);
    return m("div", {
      className: "Page DescriptionsPage"
    }, m("div", {
      className: "container"
    }, m("div", {
      className: "Page-container"
    }, m("div", {
      className: "Page-content"
    }, m("h1", {
      className: "ChampisDiscussionList-header"
    }, m("i", {
      className: "icon fas fa-align-left",
      "aria-hidden": "true"
    }), " ", app.translator.trans('champis-species.forum.descriptions_page.title')), m("div", {
      className: "IndexPage-toolbar"
    }, m("div", {
      className: "ChampisDescriptionsList-search"
    }, m("i", {
      className: "icon fas fa-search",
      "aria-hidden": "true"
    }), m("input", {
      className: "FormControl",
      type: "search",
      placeholder: extractText(app.translator.trans('champis-species.forum.descriptions_page.search_placeholder')),
      "aria-label": extractText(app.translator.trans('champis-species.forum.descriptions_page.search_placeholder')),
      value: this.query,
      oninput: e => {
        this.query = e.target.value;
        this.visibleCount = VISIBLE_STEP;
      }
    }))), m("div", {
      className: "ChampisDescriptionsList"
    }, !this.discussions && this.error ? m(Placeholder, {
      text: app.translator.trans('champis-species.forum.descriptions_page.error')
    }) : !this.discussions ? m(LoadingIndicator, null) : rows.length === 0 ? m(Placeholder, {
      text: app.translator.trans('champis-species.forum.descriptions_page.empty')
    }) : m("ul", {
      className: "ChampisDescriptionsList-items"
    }, visible.map(discussion => this.row(discussion)))), visible.length < rows.length && m("div", {
      className: "ChampisDescriptionsList-loadMore"
    }, m(Button, {
      className: "Button",
      onclick: () => this.visibleCount += VISIBLE_STEP
    }, app.translator.trans('champis-species.forum.descriptions_page.load_more_button'))), this.discussions && this.error && m("p", {
      className: "ChampisDescriptionsList-partialError"
    }, app.translator.trans('champis-species.forum.descriptions_page.partial_error'))), m("div", {
      className: "Page-sidebar"
    }, m(SpeciesSearchWidget, null), this.newDiscussionButton()))));
  }

  // Same button (icon, classes, disabled/login-fallback logic, core's own
  // translator keys) as core's IndexSidebar.tsx renders on a tag page's own
  // sidebar - a plain copy rather than reusing IndexSidebar itself, which
  // would need a routeName ('index'/'tag') this page doesn't have to get
  // past champis-forum's own restrictSidebarWidgetsToIndexPages.js gate.
  newDiscussionButton() {
    const canStartDiscussion = app.forum.attribute('canStartDiscussion') || !app.session.user;
    return m(Button, {
      icon: "fas fa-edit",
      className: "Button Button--primary IndexPage-newDiscussion",
      "aria-label": app.translator.trans(`core.forum.index.${canStartDiscussion ? 'start_discussion_button' : 'cannot_start_discussion_button'}`),
      onclick: () => startDiscussion('descriptions'),
      disabled: !canStartDiscussion
    }, app.translator.trans(`core.forum.index.${canStartDiscussion ? 'start_discussion_button' : 'cannot_start_discussion_button'}`));
  }

  // Whole row is one link (no more separate species-page icon to carve
  // space out for, see forum.less - that removed the need for the
  // "stretched link" trick too), same pattern as the homepage widgets:
  // bold title, muted secondary line below.
  row(discussion) {
    const species = discussion.species()[0];
    const user = discussion.user();
    return m("li", {
      key: discussion.id()
    }, m(Link, {
      href: app.route.description(discussion.id(), species.canonicalName),
      className: "ChampisDescriptionsList-item"
    }, m("span", {
      className: "ChampisDescriptionsList-text"
    }, m("span", {
      className: "ChampisDescriptionsList-name"
    }, speciesName(species), species.synonymCanonicalName && m("small", null, ' ', "(=", ' ', speciesName({
      canonicalName: species.synonymCanonicalName,
      author: species.synonymAuthor,
      scientificName: species.synonymCanonicalName
    }), ")")), species.vernacularName && m("span", {
      className: "ChampisDescriptionsList-vernacular"
    }, species.vernacularName), m("div", {
      className: "ChampisDescriptionsList-author"
    }, app.translator.trans('champis-species.forum.gallery.by', {
      name: authorDisplayName(user || null)
    }))), discussion.descriptionImageUrl() && m("img", {
      src: discussion.descriptionImageUrl(),
      alt: "",
      loading: "lazy"
    })));
  }
}
flarum.reg.add('champis-species', 'forum/components/DescriptionsPage', DescriptionsPage);